import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/services/api/auth';
import { useAuthStore } from '@/store/authStore';

/**
 * Keeps the cached user — and crucially their partner's capabilities — in step
 * with the server for the life of the session.
 *
 * Why this exists: capabilities arrive with the login response, which then sits
 * in localStorage until the rep signs out. So an admin switching a partner
 * setting had no effect until the rep logged out and back in, which FR-053
 * forbids. Refetching `/auth/me` closes that gap: a setting change lands on the
 * next refetch, and every capability-gated screen re-renders from the store.
 *
 * Deliberately cheap on the server (one user read plus one partner read, no
 * Salesforce), so refetching on window focus and on an interval is affordable.
 */
const REFRESH_INTERVAL_MS = 60_000;

export function useCurrentUserSync(): void {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);

  const { data } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchInterval: REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // A failure here must never sign the rep out — the 401 handler already owns
    // that. Keep showing the cached user and try again on the next tick.
    retry: 1,
  });

  useEffect(() => {
    if (data?.user) setUser(data.user);
  }, [data, setUser]);
}
